import React, { useState } from 'react'
import { Card, Tabs, Upload, Button, Tag, Spin, Typography, Dropdown } from 'antd'
import { UploadOutlined, DownloadOutlined, PrinterOutlined } from '@ant-design/icons'
import type { UploadFile } from 'antd/es/upload/interface'
import type { TabsProps } from 'antd'
import { getN8nWebhookUrl, postN8nWebhook } from '../../api/n8n'
import { generateDocumentBlob } from '../../api/documents'
import './IntelligentDiagnosis.css'

const { Title, Paragraph } = Typography

const IntelligentDiagnosis: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [analyzing, setAnalyzing] = useState(false)
  const [result, setResult] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<string>('ct')

  const diagnosisTypes = [
    { key: 'pathology', label: '病理诊断' },
    { key: 'ultrasound', label: 'B超诊断' },
    { key: 'ecg', label: '心电图诊断' },
    { key: 'ct', label: 'CT诊断' },
    { key: 'mri', label: '核磁诊断' },
  ]

  const handleAnalyze = async () => {
    if (fileList.length === 0) {
      return
    }

    setAnalyzing(true)
    setResult(null)

    try {
      // 准备 FormData，将文件发送到 n8n 工作流
      const formData = new FormData()
      const file = fileList[0]
      const fileObj = (file.originFileObj ?? file) as File
      
      if (!fileObj || !(fileObj instanceof Blob)) {
        throw new Error('文件对象不存在')
      }

      formData.append('data', fileObj)
      formData.append('fileName', fileObj.name || 'medical-image')
      formData.append('diagnosisType', diagnosisTypes.find(t => t.key === activeTab)?.label || '影像学诊断')

      const n8nWebhookUrl = getN8nWebhookUrl(
        import.meta.env.VITE_N8N_WEBHOOK_URL || 'medical-image'
      )
      console.log('调用 n8n 工作流:', n8nWebhookUrl)

      const result = await postN8nWebhook<any>(n8nWebhookUrl, formData, {
        timeoutMs: 120000,
      })
      console.log('工作流返回结果:', result)

      // 清理函数：移除"并提供中文诊断报告"、星号和英文标题
      const cleanText = (text: string): string => {
        if (!text) return text
        let cleaned = text
          // 删除"并提供中文诊断报告"及其变体（多次替换确保完全清除）
          .replace(/并提供中文诊断报告[。.]?/gi, '')
          .replace(/并提供中文[^。.]*报告[。.]?/gi, '')
          .replace(/并提供[^。.]*诊断报告[。.]?/gi, '')
          .replace(/并提供[^。.]*报告[。.]?/gi, '')
          .replace(/并提供中文检测报告[。.]?/gi, '')
          .replace(/并提供[^。.]*检测报告[。.]?/gi, '')
          .replace(/并提供中文[^。.]*诊断[^。.]*报告[。.]?/gi, '')
          // 删除所有星号（彻底清理）
          .replace(/\*/g, '') // 先删除所有星号字符
          .replace(/\s*\*\s*/g, '') // 删除带空格的星号
          .replace(/^\*\s*/gm, '') // 删除行首的星号
          .replace(/\s*\*$/gm, '') // 删除行尾的星号
          // 替换英文标题为中文标题
          .replace(/^Imaging Findings[：:]\s*/gmi, '')
          .replace(/^Findings[：:]\s*/gmi, '')
          .replace(/^Diagnosis[：:]\s*/gmi, '')
          .replace(/^Diagnostic Opinion[：:]\s*/gmi, '')
          .replace(/^Opinion[：:]\s*/gmi, '')
          .replace(/^Recommendations[：:]\s*/gmi, '')
          .replace(/^Recommendation[：:]\s*/gmi, '')
          .replace(/^Suggestions[：:]\s*/gmi, '')
          .replace(/^Suggestion[：:]\s*/gmi, '')
          // 清理多余的空格
          .replace(/\s{2,}/g, ' ') // 多个空格替换为单个
          .trim()
        
        // 再次检查并清理（确保完全清除）
        if (cleaned.includes('并提供') || cleaned.includes('*')) {
          cleaned = cleaned
            .replace(/并提供[^。.]*报告[。.]?/gi, '')
            .replace(/\*/g, '')
            .trim()
        }
        
        return cleaned
      }

      // 解析诊断结果
      let findings = result.findings || result.analysisResult || result.analysis || result.result || result.response || ''
      let diagnosisOpinion = result.diagnosisOpinion || ''
      let reportMarkdown = result.reportMarkdown || ''

      // 清理文本内容
      findings = cleanText(findings)
      diagnosisOpinion = cleanText(diagnosisOpinion)
      // 清理reportMarkdown中的内容（但保留Markdown格式）
      if (reportMarkdown) {
        // 按行处理，清理文本内容但保留Markdown标记
        const lines = reportMarkdown.split('\n')
        const cleanedLines = lines.map(line => {
          // 跳过Markdown标题、列表等格式行
          if (line.trim().match(/^#{1,6}\s/) || // Markdown标题
              line.trim().match(/^[-*+]\s/) || // Markdown列表
              line.trim().match(/^\d+\.\s/) || // 有序列表
              line.trim().match(/^---/) || // 分隔线
              line.trim().match(/^\*\*/)) { // 粗体标记
            return line
          }
          // 清理普通文本行
          return line
            .replace(/并提供中文诊断报告[。.]?/gi, '')
            .replace(/并提供中文[^。.]*报告[。.]?/gi, '')
            .replace(/并提供[^。.]*诊断报告[。.]?/gi, '')
            .replace(/并提供[^。.]*报告[。.]?/gi, '')
            .replace(/并提供中文检测报告[。.]?/gi, '')
            .replace(/并提供[^。.]*检测报告[。.]?/gi, '')
            // 删除单独的星号（不在列表标记中的）
            .replace(/\s+\*\s+/g, ' ') // 删除中间单独的星号
            .replace(/^\*\s+(?![*-\d])/gm, '') // 删除行首单独的星号（不是列表标记）
            .replace(/\s+\*$/gm, '') // 删除行尾的星号
        })
        reportMarkdown = cleanedLines.join('\n')
      }

      const documents = result.documents || {}
      const documentsGenerated = result.documentsGenerated || {}

      // 判断诊断结果（正常/异常）
      const diagnosisStatus = diagnosisOpinion.includes('正常') || diagnosisOpinion.includes('未见异常') 
        ? '正常' 
        : (diagnosisOpinion.includes('异常') || diagnosisOpinion.includes('病变') ? '异常' : '待定')

      setResult({
        diagnosis: diagnosisStatus,
        diagnosisOpinion: diagnosisOpinion || '基于AI智能分析，建议结合临床病史和其他检查结果进行综合判断',
        findings: findings, // 影像所见（纯影像描述，已清理）
        suggestions: '建议结合临床症状和体征进行综合诊断，如有异常发现，建议进一步检查或专科会诊',
        reportMarkdown: reportMarkdown,
        documents: documents,
        documentsGenerated: documentsGenerated,
        documentGenerationErrors: result.documentGenerationErrors,
      })
    } catch (error: any) {
      console.error('AI分析失败:', error)
      setResult({
        diagnosis: '错误',
        findings: `分析失败: ${error.message || '未知错误'}`,
        suggestions: '请检查文件格式是否正确，或稍后重试',
      })
    } finally {
      setAnalyzing(false)
    }
  }

  const handleDownloadReport = async (format: 'markdown' | 'docx' | 'pdf') => {
    if (!result) {
      console.error('没有可下载的报告')
      return
    }

    try {
      let blob: Blob

      // 如果有生成的文档（base64格式），使用它
      if (result.documents && result.documents[format]) {
        const base64Data = result.documents[format]
        const byteCharacters = atob(base64Data)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        blob = new Blob([byteArray], { 
          type: format === 'pdf' ? 'application/pdf' : 
                format === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' :
                'text/markdown'
        })
      } 
      // 如果没有生成的文档，但有Markdown内容
      else if (result.reportMarkdown) {
        // 对于markdown格式，直接下载文本
        if (format === 'markdown') {
          blob = new Blob([result.reportMarkdown], { type: 'text/markdown;charset=utf-8' })
        } 
        // 对于DOCX和PDF格式，尝试调用后端API实时生成
        else {
          try {
            blob = await generateDocumentBlob({
              content: result.reportMarkdown,
              format,
              documentType: 'chapter',
              title: result.reportTitle || '诊断报告',
              author: '岐黄智医AI诊断系统',
            })
          } catch (apiError: unknown) {
            console.error(`实时生成${format.toUpperCase()}失败:`, apiError)
            alert(
              `${format.toUpperCase()}格式报告生成失败: ${(apiError as Error).message || '请稍候再试'}`
            )
            return
          }
        }
      } 
      else {
        alert('报告内容不可用，请重新分析')
        return
      }

      // 创建下载链接
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const fileName = result.reportTitle || `诊断报告_${new Date().toISOString().split('T')[0]}`
      a.download = `${fileName}.${format === 'markdown' ? 'md' : format}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error: any) {
      console.error(`下载${format}报告失败:`, error)
      alert(`下载失败: ${error.message}`)
    }
  }

  const tabItems: TabsProps['items'] = diagnosisTypes.map((type) => ({
    key: type.key,
    label: type.label,
    children: (
      <div className="diagnosis-upload">
        <Upload
          accept=".dcm,.dicom,.jpg,.jpeg,.png,.bmp,.tiff,.tif"
          fileList={fileList}
          onChange={({ fileList }) => setFileList(fileList)}
          beforeUpload={() => false}
        >
          <Button icon={<UploadOutlined />} size="large">
            上传DICOM或图片
          </Button>
        </Upload>
        <p className="upload-hint">支持DICOM格式文件或图片格式（JPG、PNG、BMP、TIFF等）</p>

        {fileList.length > 0 && (
          <Button
            type="primary"
            size="large"
            onClick={handleAnalyze}
            loading={analyzing}
            className="analyze-button"
          >
            开始AI分析
          </Button>
        )}

        {analyzing && (
          <div className="analyzing">
            <Spin size="large" />
            <p>AI正在分析中，请稍候...</p>
          </div>
        )}

        {result && (
          <Card 
            className="diagnosis-result" 
            title={<span className="diagnosis-report-title">诊断报告</span>}
            extra={<span className="ai-generated-badge">AI生成</span>}
          >
            <div className="result-section">
              <h4>影像所见</h4>
              <p className="findings-content">{result.findings || '暂无影像所见内容'}</p>
            </div>
            <div className="result-section">
              <h4>诊断意见</h4>
              <div className="diagnosis-opinion">
                <Tag color={result.diagnosis === '正常' ? 'green' : result.diagnosis === '异常' ? 'red' : 'orange'}>
                  {result.diagnosis}
                </Tag>
                {result.diagnosisOpinion && (
                  <p className="diagnosis-text">{result.diagnosisOpinion}</p>
                )}
              </div>
            </div>
            <div className="result-section">
              <h4>建议</h4>
              <p>{result.suggestions}</p>
            </div>
            <div className="ai-disclaimer">
              <span className="ai-disclaimer-text">* 本报告由AI智能诊断系统生成，仅供参考，不能替代专业医生的诊断。</span>
            </div>
            <div className="result-actions">
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'markdown',
                      label: '下载 Markdown',
                      icon: <DownloadOutlined />,
                    },
                    {
                      key: 'docx',
                      label: '下载 DOCX',
                      icon: <DownloadOutlined />,
                    },
                    {
                      key: 'pdf',
                      label: '下载 PDF',
                      icon: <DownloadOutlined />,
                    },
                  ],
                  onClick: ({ key }) => {
                    handleDownloadReport(key as 'markdown' | 'docx' | 'pdf')
                  },
                }}
                placement="bottomLeft"
              >
                <Button 
                  type="primary"
                  icon={<DownloadOutlined />}
                >
                  下载报告
                </Button>
              </Dropdown>
              {result.reportMarkdown && (
                <Button 
                  icon={<PrinterOutlined />} 
                  onClick={() => window.print()}
                >
                  打印报告
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    ),
  }))

  return (
    <div className="intelligent-diagnosis-page">
      <div className="diagnosis-header">
        <Title level={2}>影像学诊断</Title>
        <Paragraph>
          提供病理诊断、心电图诊断、B超诊断、CT诊断、核磁诊断等医学影像智能分析功能
        </Paragraph>
      </div>
      <Card className="diagnosis-card">
        <Tabs 
          defaultActiveKey="ct" 
          items={tabItems}
          onChange={(key) => setActiveTab(key)}
        />
      </Card>
    </div>
  )
}

export default IntelligentDiagnosis

